<script setup>
// NOTICE: KAPTION-INTEGRATION
// WhatsApp Preview component using wapreview.kaptionai.com iframe
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import themeEventEmitter from 'shared/helpers/themeEventEmitter';

const props = defineProps({
  // The full template object from WhatsApp
  template: {
    type: Object,
    default: null,
  },
  // Rendered body text (with variables replaced)
  renderedBody: {
    type: String,
    default: '',
  },
  // Loading state
  loading: {
    type: Boolean,
    default: false,
  },
  // Show bare mode (message only) or full phone frame
  bare: {
    type: Boolean,
    default: true,
  },
  // Company name to show in phone header
  companyName: {
    type: String,
    default: 'Business',
  },
});

// NOTICE: KAPTION-INTEGRATION - whatsapp-flows 0.3: wapreview dropped /render
// (everything lives on /show), so the old URL rendered a blank frame. /show
// takes a flat template config ({header, headerFormat, body, footer, buttons,
// category}) through UPDATE_TEMPLATE {template} and SET_THEME / SET_LOADING.
const PREVIEW_URL = 'https://wapreview.kaptionai.com/show';

const iframeRef = ref(null);
const iframeLoaded = ref(false);

// Detect current theme
const isDarkMode = ref(document.body.classList.contains('dark'));

const headerExampleUrl = component =>
  component.example?.header_handle?.[0] ||
  component.example?.header_url?.[0] ||
  (Array.isArray(component.example) ? component.example[0] : undefined);

// Build the flat template config wapreview's /show expects.
const templateConfig = computed(() => {
  if (!props.template) return null;

  const config = {
    category: props.template.category || 'UTILITY',
    body: '',
  };

  props.template.components?.forEach(component => {
    if (component.type === 'HEADER') {
      const format = component.format || 'TEXT';
      if (format === 'TEXT') {
        config.headerFormat = 'TEXT';
        config.header = component.text || '';
      } else if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(format)) {
        config.headerFormat = format === 'DOCUMENT' ? 'DOCUMENT' : 'IMAGE';
        const exampleUrl = headerExampleUrl(component);
        if (exampleUrl) config.headerMediaUrl = exampleUrl;
        if (format === 'DOCUMENT') {
          config.headerDocumentFilename =
            component.document_filename || 'Document.pdf';
        }
      }
    } else if (component.type === 'BODY') {
      // Use rendered body if available, otherwise use original
      config.body = props.renderedBody || component.text || '';
    } else if (component.type === 'FOOTER') {
      config.footer = component.text || '';
    } else if (component.type === 'BUTTONS') {
      config.buttons =
        component.buttons?.map(btn => ({
          type: btn.type || 'QUICK_REPLY',
          text: btn.text || '',
          ...(btn.url && { url: btn.url }),
          ...(btn.phone_number && { phone_number: btn.phone_number }),
          // whatsapp-flows: keep what wapreview needs to draw a FLOW button.
          ...(btn.flow_id && { flow_id: btn.flow_id }),
          ...(btn.navigate_screen && { navigate_screen: btn.navigate_screen }),
        })) || [];
    }
  });

  return config;
});

const post = message => {
  iframeRef.value?.contentWindow?.postMessage(message, '*');
};

// Send template data to iframe via postMessage
const sendTemplateToIframe = () => {
  if (!iframeRef.value || !iframeLoaded.value) return;

  post({ type: 'SET_THEME', theme: isDarkMode.value ? 'dark' : 'light' });
  if (props.loading) {
    post({ type: 'SET_LOADING', loading: true });
    return;
  }
  // wapreview reads template.headerFormat: never send a null template.
  if (templateConfig.value) {
    post({ type: 'UPDATE_TEMPLATE', template: templateConfig.value });
    post({ type: 'SET_LOADING', loading: false });
  }
};

// Listen for theme changes
const handleThemeChange = event => {
  isDarkMode.value = event.theme === 'dark';
  sendTemplateToIframe();
};

let unsubscribeTheme = null;

onMounted(() => {
  unsubscribeTheme = themeEventEmitter.onThemeChange(handleThemeChange);
});

onUnmounted(() => {
  if (unsubscribeTheme) {
    unsubscribeTheme();
  }
});

// Handle iframe load
const handleIframeLoad = () => {
  iframeLoaded.value = true;
  // Small delay to ensure iframe is ready to receive messages
  setTimeout(() => {
    sendTemplateToIframe();
  }, 100);
};

// Watch for changes and update iframe
watch(
  [() => props.template, () => props.renderedBody, () => props.loading],
  () => {
    sendTemplateToIframe();
  },
  { deep: true }
);

// Iframe URL with bare mode and initial theme
const iframeSrc = computed(() => {
  const params = new URLSearchParams({
    bare: props.bare ? 'true' : 'false',
    mode: 'render',
    loading: 'false',
    dark: isDarkMode.value ? 'true' : 'false',
  });
  // Add company name for phone frame mode
  if (!props.bare && props.companyName) {
    params.set('company_name', props.companyName);
  }
  return `${PREVIEW_URL}?${params.toString()}`;
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <div
    class="kaption-whatsapp-preview relative overflow-hidden rounded-xl"
    :class="[
      bare
        ? isDarkMode
          ? 'bg-[#0b141a]'
          : 'bg-[#efeae2]'
        : isDarkMode
          ? 'bg-[#0b141a]'
          : 'bg-[#e5ddd5]',
      { 'with-pattern': bare },
    ]"
  >
    <!-- Loading overlay -->
    <div
      v-if="loading || (template && !iframeLoaded)"
      class="absolute inset-0 flex items-center justify-center z-10"
      :class="
        bare
          ? isDarkMode
            ? 'bg-[#0b141a]'
            : 'bg-[#efeae2]'
          : 'bg-n-alpha-black1'
      "
    >
      <div class="flex flex-col items-center gap-2">
        <div
          class="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
          :class="isDarkMode ? 'border-[#00a884]' : 'border-[#25d366]'"
        />
        <span
          class="text-xs"
          :class="isDarkMode ? 'text-[#8696a0]' : 'text-[#667781]'"
        >
          Loading preview...
        </span>
      </div>
    </div>

    <!-- Empty state -->
    <div
      v-if="!template && !loading"
      class="flex items-center justify-center h-full min-h-[200px]"
    >
      <span
        class="text-sm"
        :class="isDarkMode ? 'text-[#8696a0]' : 'text-[#667781]'"
      >
        Select a template to preview
      </span>
    </div>

    <!-- iframe preview -->
    <iframe
      v-show="template && !loading"
      ref="iframeRef"
      :src="iframeSrc"
      class="w-full h-full min-h-[200px] border-0 rounded-xl"
      title="WhatsApp Template Preview"
      sandbox="allow-scripts allow-same-origin"
      @load="handleIframeLoad"
    />
  </div>
  <!-- eslint-enable vue/no-bare-strings-in-template -->
</template>

<style scoped>
/* WhatsApp-like chat background pattern - only for bare mode */
.kaption-whatsapp-preview.with-pattern {
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

.dark .kaption-whatsapp-preview.with-pattern {
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}
</style>
